import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Add Authorization header from stored token (cross-origin fallback)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/')) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const refreshResp = await api.post('/auth/refresh', refreshToken ? { refresh_token: refreshToken } : {});
        const newToken = refreshResp.data?.access_token;
        if (newToken) {
          localStorage.setItem('access_token', newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      } catch {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  verifyEmail: (code) => api.post('/auth/verify-email', { code }),
  resendVerification: () => api.post('/auth/resend-verification'),
};

// Reports
export const reportAPI = {
  upload: (formData) => api.post('/reports/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  list: () => api.get('/reports/'),
  get: (id) => api.get(`/reports/${id}`),
  delete: (id) => api.delete(`/reports/${id}`),
  undoDelete: (id) => api.post(`/reports/${id}/undo-delete`),
  reanalyze: (id) => api.post(`/reports/${id}/reanalyze`),
};

// Chat
export const chatAPI = {
  send: (reportId, message) => api.post(`/chat/${reportId}`, { message }),
  history: (reportId) => api.get(`/chat/${reportId}`),
};

// Comparison
export const compareAPI = {
  compare: (reportAId, reportBId) => api.post('/compare/', { report_a_id: reportAId, report_b_id: reportBId }),
  list: () => api.get('/compare/'),
};

// Trends
export const trendsAPI = {
  get: (category) => api.get('/trends/', { params: category ? { category } : {} }),
  summary: () => api.get('/trends/summary'),
};

// Smart Alerts
export const alertsAPI = {
  list: () => api.get('/alerts/'),
  latest: () => api.get('/alerts/latest'),
};

// Consent
export const consentAPI = {
  current: () => api.get('/consent/current'),
  accept: (data) => api.post('/consent/accept', data),
  status: () => api.get('/consent/status'),
};

// Chat Export
export const chatExportAPI = {
  export: (reportId) => api.get(`/chat/${reportId}/export`),
};

// Report Summary Export
export const reportExportAPI = {
  summary: (reportId) => api.get(`/reports/${reportId}/summary-export`),
};

// Notifications
export const notificationAPI = {
  list: () => api.get('/notifications/'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/mark-all-read'),
  unreadCount: () => api.get('/notifications/unread-count'),
  registerDevice: (token) => api.post('/notifications/register-device', { token }),
  unregisterDevice: (token) => api.delete('/notifications/unregister-device', { data: { token } }),
  pushStatus: () => api.get('/notifications/push-status'),
};

// Feedback
export const feedbackAPI = {
  submit: (data) => api.post('/feedback/', data),
  list: () => api.get('/feedback/'),
};

// Share
export const shareAPI = {
  create: (reportId, data) => api.post(`/share/reports/${reportId}`, data),
  links: (reportId) => api.get(`/share/reports/${reportId}/links`),
  revoke: (shareId) => api.post(`/share/revoke/${shareId}`),
};

// Data Export
export const exportAPI = {
  request: () => api.post('/export/'),
};

// Account
export const accountAPI = {
  delete: () => api.delete('/account'),
};

// Admin
export const adminAPI = {
  dashboard: () => api.get('/admin/dashboard'),
  users: () => api.get('/admin/users'),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  forceLogout: (id) => api.post(`/admin/users/${id}/force-logout`),
  sendNotification: (data) => api.post('/admin/notifications', data),
  scheduledNotifications: () => api.get('/admin/notifications/scheduled'),
  cancelScheduled: (id) => api.delete(`/admin/notifications/scheduled/${id}`),
  reports: () => api.get('/admin/reports'),
  auditLogs: (params) => api.get('/admin/audit-logs', { params }),
  chatSafetyLogs: () => api.get('/admin/chat-safety-logs'),
  feedback: () => api.get('/admin/feedback'),
  getReport: (id) => api.get(`/admin/reports/${id}`),
  smartAlerts: () => api.get('/admin/smart-alerts'),
  rateControlStats: () => api.get('/admin/rate-control/stats'),
  rateControlAlerts: () => api.get('/admin/rate-control/alerts'),
  deleteRequests: () => api.get('/admin/delete-requests'),
  requestDelete: (reportId, reason) => api.post(`/admin/reports/${reportId}/request-delete`, { reason }),
  approveDelete: (requestId) => api.post(`/admin/delete-requests/${requestId}/approve`),
  rejectDelete: (requestId) => api.post(`/admin/delete-requests/${requestId}/reject`),
  apiKeysStatus: () => api.get('/admin/api-keys/status'),
  markKeyRotated: (service) => api.post(`/admin/api-keys/${service}/mark-rotated`),
  aiUsage: () => api.get('/admin/ai-usage'),
  aiUsageByUser: () => api.get('/admin/ai-usage/by-user'),
  aiHealth: () => api.get('/admin/ai-health'),
  feedbackAnalytics: () => api.get('/admin/feedback/analytics'),
  updateFeedbackStatus: (id, status) => api.put(`/admin/feedback/${id}/status`, { status }),
  dbBackup: () => api.get('/admin/db/backup'),
  dbStats: () => api.get('/admin/db/stats'),
};

// Region/Jurisdiction
export const regionAPI = {
  list: () => api.get('/region/list'),
  me: () => api.get('/region/me'),
  set: (region) => api.post('/region/set', { region }),
};

// System
export const systemAPI = {
  categories: () => api.get('/system/categories'),
  reportStates: () => api.get('/system/report-states'),
};

// File URL helper
export const getFileUrl = (reportId) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    return `${API_URL}/api/v1/files/${reportId}?token=${token}`;
  }
  return `${API_URL}/api/v1/files/${reportId}`;
};

export function formatApiError(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;

  // Friendly messages for common HTTP errors
  if (status === 502 || status === 503) return 'Server is temporarily unavailable. Please try again in a moment.';
  if (status === 500) return 'Something went wrong on our end. Please try again.';
  if (status === 429) return detail || 'Too many requests. Please wait a moment and try again.';
  if (status === 401) return detail || 'Your session has expired. Please log in again.';
  if (status === 403) return detail || 'You don\'t have permission to do that.';
  if (status === 404) return detail || 'The requested item was not found.';

  // Use backend detail message if available
  if (detail) {
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || '').filter(Boolean).join('. ') || 'Please check your input and try again.';
    return String(detail);
  }

  // Network errors (no response from server)
  if (error?.code === 'ERR_NETWORK' || !error?.response) return 'Unable to connect to the server. Please check your internet connection.';

  return 'Something went wrong. Please try again.';
}

export default api;
